import { useParams, useNavigate } from 'react-router-dom';
import type { Project } from '@/types/entities';
import { PageHeader } from '@/components/composites/PageHeader';
import { EmployeeSwitcher } from '@/components/composites/EmployeeSwitcher';
import { DataTable } from '@/components/composites/DataTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { Badge } from '@/components/primitives/Badge';
import { ActionLink } from '@/components/primitives/ActionLink';
import { useEmployeesWithStats } from '@/hooks/useEmployees';
import { useClients } from '@/hooks/useClients';
import { noop } from '@/lib/noop';
import styles from './EmployeeClientProjectsPage.module.css';

interface ProjectRowData {
  project: Project;
  clientName: string;
  totalHours: number;
  billableHours: number;
  events: number;
}

export function EmployeeClientProjectsPage() {
  const { employeeId, clientId } = useParams<{ employeeId: string; clientId: string }>();
  const navigate = useNavigate();
  const { data: employeesWithStats } = useEmployeesWithStats();
  const { data: clients } = useClients();
  const employee = employeesWithStats.find((e) => e.id === employeeId);
  const client = clients.find((c) => c.id === clientId);

  if (!employee || !client) {
    return (
      <>
        <PageHeader title="Not found" />
        <EmptyState
          title="Employee or client not found"
          description="This combination does not exist in the mockup tenant."
        />
      </>
    );
  }

  const projectCount = Math.max(1, client.projects.length);
  const totalBase = employee.totalHours / 3;
  const billableBase = employee.billableHours / 3;
  const eventsBase = employee.events / 3;

  const rows: ProjectRowData[] = client.projects.map((p, i) => {
    const share = 1 / projectCount;
    const adjust = i === 0 ? 1.2 : 1;
    return {
      project: p,
      clientName: client.name,
      totalHours: +(totalBase * share * adjust).toFixed(1),
      billableHours: +(billableBase * share * adjust).toFixed(1),
      events: Math.round(eventsBase * share * adjust),
    };
  });

  const totalHours = rows.reduce((s, r) => s + r.totalHours, 0);
  const totalBillable = rows.reduce((s, r) => s + r.billableHours, 0);
  const totalEvents = rows.reduce((s, r) => s + r.events, 0);

  return (
    <>
      <PageHeader
        title={`${client.name} · Projects`}
        subtitle={`${employee.name}`}
        actions={
          <EmployeeSwitcher
            currentEmployeeId={employeeId ?? ''}
            onSwitch={(id) => navigate(`/employees/${id}/clients/${clientId}/projects`)}
          />
        }
      />
      {/* Column order per Rule 22: Project, Client, Type, [data columns], Actions */}
      <DataTable<ProjectRowData>
        caption={`Projects for ${employee.name} at ${client.name}`}
        columns={[
          {
            key: 'project',
            header: 'Project',
            render: (row) => <span className={styles.projectName}>{row.project.name}</span>,
          },
          { key: 'client', header: 'Client', render: (row) => <span>{row.clientName}</span> },
          {
            key: 'type',
            header: 'Type',
            render: (row) => (
              <Badge variant={row.project.type}>
                {row.project.type[0].toUpperCase() + row.project.type.slice(1)}
              </Badge>
            ),
          },
          {
            key: 'totalHours',
            header: 'Total Hours',
            align: 'right',
            render: (row) => <span>{row.totalHours.toFixed(1)}h</span>,
          },
          {
            key: 'billableHours',
            header: 'Billable Hours',
            align: 'right',
            render: (row) => <span>{row.billableHours.toFixed(1)}h</span>,
          },
          {
            key: 'events',
            header: 'Events',
            align: 'right',
            render: (row) => <span>{row.events.toLocaleString()}</span>,
          },
          {
            key: 'actions',
            header: 'Actions',
            align: 'right',
            render: () => <ActionLink onClick={noop}>View</ActionLink>,
          },
        ]}
        rows={rows}
        getRowKey={(row) => row.project.id}
        footer={
          <tr>
            <td>Total</td>
            <td />
            <td />
            <td style={{ textAlign: 'right' }}>{totalHours.toFixed(1)}h</td>
            <td style={{ textAlign: 'right' }}>{totalBillable.toFixed(1)}h</td>
            <td style={{ textAlign: 'right' }}>{totalEvents.toLocaleString()}</td>
            <td />
          </tr>
        }
      />
    </>
  );
}
